/**
 * Offer Form Dialog Component
 * 구매자가 판매자에게 제안을 제출하는 폼
 */

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import { Handshake, Loader2 } from "lucide-react";
import { useState } from "react";

interface OfferFormDialogProps {
  productId: number;
  productPriceCents?: number;
  negotiable?: boolean; // 네고 가능 여부
  pickupPlaces?: Array<{ id: number; name: string; address?: string }>;
  pickupTimes?: Array<{ id: number; day: string; start: string; end: string }>;
  paymentMethods?: string[];
  onSuccess?: () => void;
  trigger?: React.ReactNode;
}

export function OfferFormDialog({
  productId,
  productPriceCents,
  negotiable = false,
  pickupPlaces = [],
  pickupTimes = [],
  paymentMethods = [],
  onSuccess,
  trigger,
}: OfferFormDialogProps) {
  const [open, setOpen] = useState(false);
  const [priceCents, setPriceCents] = useState<number | "">(
    productPriceCents || ""
  );
  const [selectedPickupPlace, setSelectedPickupPlace] = useState<number | "">(
    ""
  );
  const [pickupPlaceCustom, setPickupPlaceCustom] = useState<string>("");
  const [useCustomPlace, setUseCustomPlace] = useState(false);
  const [selectedPickupTime, setSelectedPickupTime] = useState<number | "">("");
  const [pickupTimeCustom, setPickupTimeCustom] = useState<string>("");
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>("");
  const [memo, setMemo] = useState("");
  const { toast } = useToast();

  const submitOfferMutation = useMutation({
    mutationFn: async () => {
      // 가격 검증 (negotiable인 경우만 필수)
      // negotiable이 false인 경우 가격 제안 불가
      if (!negotiable && priceCents && typeof priceCents === "number") {
        throw new Error("이 상품은 가격 제안이 불가능합니다");
      }
      if (negotiable && (!priceCents || typeof priceCents !== "number")) {
        throw new Error("가격을 입력해주세요");
      }

      const body: {
        product_id: number;
        price_cents?: number;
        pickup_place_id?: number;
        pickup_place_custom?: string;
        pickup_time_id?: number;
        pickup_time_custom?: string;
        payment_method?: string;
        memo?: string;
      } = {
        product_id: productId,
      };

      // 가격 (negotiable인 경우만)
      if (negotiable && priceCents && typeof priceCents === "number") {
        body.price_cents = priceCents;
      }

      // 픽업 장소 (선택 또는 직접 입력)
      if (pickupPlaces.length > 0) {
        // 판매자가 지정한 장소가 있는 경우
        if (useCustomPlace && pickupPlaceCustom.trim()) {
          body.pickup_place_custom = pickupPlaceCustom.trim();
        } else if (
          selectedPickupPlace &&
          typeof selectedPickupPlace === "number"
        ) {
          body.pickup_place_id = selectedPickupPlace;
        }
      } else if (pickupPlaceCustom.trim()) {
        // 판매자가 지정한 장소가 없는 경우 직접 입력만 가능
        body.pickup_place_custom = pickupPlaceCustom.trim();
      }

      // 픽업 시간 (선택 또는 직접 입력)
      if (pickupTimes.length > 0) {
        // 판매자가 지정한 시간이 있는 경우
        if (useCustomTime && pickupTimeCustom.trim()) {
          body.pickup_time_custom = pickupTimeCustom.trim();
        } else if (
          selectedPickupTime &&
          typeof selectedPickupTime === "number"
        ) {
          body.pickup_time_id = selectedPickupTime;
        }
      } else if (pickupTimeCustom.trim()) {
        // 판매자가 지정한 시간이 없는 경우 직접 입력만 가능
        body.pickup_time_custom = pickupTimeCustom.trim();
      }

      if (selectedPaymentMethod) {
        body.payment_method = selectedPaymentMethod;
      }

      if (memo.trim()) {
        body.memo = memo.trim();
      }

      return apiRequest("POST", "/api/market/offers", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/market/offers"] });
      setOpen(false);
      setPriceCents(productPriceCents || "");
      setSelectedPickupPlace("");
      setPickupPlaceCustom("");
      setUseCustomPlace(false);
      setSelectedPickupTime("");
      setPickupTimeCustom("");
      setUseCustomTime(false);
      setSelectedPaymentMethod("");
      setMemo("");
      toast({
        title: "제안이 제출되었습니다",
        description: "판매자가 확인하면 알림을 받으실 수 있습니다",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "제안 제출 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(0)} AUD`;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="w-full" size="lg">
            <Handshake className="w-4 h-4 mr-2" />
            구매 제안하기
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>구매 제안</DialogTitle>
          <DialogDescription>
            판매자에게 구매 조건을 제안해주세요
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* 가격 - negotiable인 경우만 표시 */}
          {negotiable && (
            <div className="space-y-2">
              <Label htmlFor="price">제안 가격 (AUD)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="price"
                  type="number"
                  placeholder="예: 400"
                  value={priceCents === "" ? "" : priceCents / 100}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setPriceCents("");
                    } else {
                      const cents = Math.round(parseFloat(value) * 100);
                      if (!isNaN(cents) && cents >= 0) {
                        setPriceCents(cents);
                      }
                    }
                  }}
                  min="0"
                  step="0.01"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  AUD
                </span>
              </div>
              {productPriceCents && (
                <p className="text-xs text-muted-foreground">
                  판매가: {formatPrice(productPriceCents)}
                </p>
              )}
            </div>
          )}
          {!negotiable && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                이 상품은 가격 제안이 불가능합니다.
              </p>
            </div>
          )}

          {/* 픽업 장소 */}
          <div className="space-y-2">
            <Label>픽업 장소</Label>
            {pickupPlaces.length > 0 ? (
              <>
                <RadioGroup
                  value={
                    useCustomPlace
                      ? "custom"
                      : selectedPickupPlace === ""
                      ? ""
                      : String(selectedPickupPlace)
                  }
                  onValueChange={(value) => {
                    if (value === "custom") {
                      setUseCustomPlace(true);
                      setSelectedPickupPlace("");
                    } else {
                      setUseCustomPlace(false);
                      setSelectedPickupPlace(value === "" ? "" : Number(value));
                    }
                  }}
                >
                  {pickupPlaces.map((place) => (
                    <div key={place.id} className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={String(place.id)}
                        id={`place-${place.id}`}
                      />
                      <Label
                        htmlFor={`place-${place.id}`}
                        className="font-normal cursor-pointer"
                      >
                        {place.name}
                        {place.address && (
                          <span className="text-xs text-muted-foreground ml-1">
                            ({place.address})
                          </span>
                        )}
                      </Label>
                    </div>
                  ))}
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="custom" id="place-custom" />
                    <Label
                      htmlFor="place-custom"
                      className="font-normal cursor-pointer"
                    >
                      직접 입력
                    </Label>
                  </div>
                </RadioGroup>
                {useCustomPlace && (
                  <Input
                    placeholder="픽업 장소를 입력해주세요"
                    value={pickupPlaceCustom}
                    onChange={(e) => setPickupPlaceCustom(e.target.value)}
                  />
                )}
              </>
            ) : (
              <Input
                placeholder="픽업 장소를 입력해주세요"
                value={pickupPlaceCustom}
                onChange={(e) => setPickupPlaceCustom(e.target.value)}
              />
            )}
          </div>

          {/* 픽업 시간 */}
          <div className="space-y-2">
            <Label>픽업 시간</Label>
            {pickupTimes.length > 0 ? (
              <>
                <RadioGroup
                  value={
                    useCustomTime
                      ? "custom"
                      : selectedPickupTime === ""
                      ? ""
                      : String(selectedPickupTime)
                  }
                  onValueChange={(value) => {
                    if (value === "custom") {
                      setUseCustomTime(true);
                      setSelectedPickupTime("");
                    } else {
                      setUseCustomTime(false);
                      setSelectedPickupTime(value === "" ? "" : Number(value));
                    }
                  }}
                >
                  {pickupTimes.map((time) => (
                    <div key={time.id} className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={String(time.id)}
                        id={`time-${time.id}`}
                      />
                      <Label
                        htmlFor={`time-${time.id}`}
                        className="font-normal cursor-pointer"
                      >
                        {time.day} {time.start} - {time.end}
                      </Label>
                    </div>
                  ))}
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="custom" id="time-custom" />
                    <Label
                      htmlFor="time-custom"
                      className="font-normal cursor-pointer"
                    >
                      직접 입력
                    </Label>
                  </div>
                </RadioGroup>
                {useCustomTime && (
                  <Input
                    placeholder="픽업 시간을 입력해주세요 (예: 토요일 오후 2시)"
                    value={pickupTimeCustom}
                    onChange={(e) => setPickupTimeCustom(e.target.value)}
                  />
                )}
              </>
            ) : (
              <Input
                placeholder="픽업 시간을 입력해주세요 (예: 토요일 오후 2시)"
                value={pickupTimeCustom}
                onChange={(e) => setPickupTimeCustom(e.target.value)}
              />
            )}
          </div>

          {/* 결제 방법 */}
          {paymentMethods.length > 0 && (
            <div className="space-y-2">
              <Label>결제 방법</Label>
              <RadioGroup
                value={selectedPaymentMethod}
                onValueChange={setSelectedPaymentMethod}
              >
                {paymentMethods.map((method) => (
                  <div key={method} className="flex items-center space-x-2">
                    <RadioGroupItem value={method} id={`payment-${method}`} />
                    <Label
                      htmlFor={`payment-${method}`}
                      className="font-normal cursor-pointer"
                    >
                      {method}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* 메모 */}
          <div className="space-y-2">
            <Label htmlFor="memo">메모 (선택사항)</Label>
            <Textarea
              id="memo"
              placeholder="추가로 전달하고 싶은 내용이 있으시면 입력해주세요"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              rows={3}
            />
          </div>

          {/* 제출 버튼 */}
          <Button
            className="w-full"
            onClick={() => submitOfferMutation.mutate()}
            disabled={
              submitOfferMutation.isPending ||
              (negotiable && (!priceCents || typeof priceCents !== "number"))
            }
          >
            {submitOfferMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                제출 중...
              </>
            ) : (
              <>
                <Handshake className="w-4 h-4 mr-2" />
                제안 제출
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
